import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  LinearProgress,
  Divider,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { EvidenceSummary, FieldSuggestion } from '../../services/api';

interface EvidenceDisplayProps {
  evidenceSummary?: EvidenceSummary;
  fieldSuggestions?: FieldSuggestion[];
  confidence?: number;
}

export function ConfidenceBadge({ confidence }: { confidence?: number }) {
  if (confidence === undefined || confidence === null) {
    return null;
  }

  const percentage = Math.round(confidence * 100);
  const level = confidence >= 0.85 ? 'HIGH' : confidence >= 0.70 ? 'MEDIUM' : 'LOW';
  
  const colors = {
    HIGH: { bg: '#D1FAE5', text: '#065F46', border: '#10B981' },
    MEDIUM: { bg: '#FEF3C7', text: '#92400E', border: '#F59E0B' },
    LOW: { bg: '#FEE2E2', text: '#991B1B', border: '#EF4444' },
  };

  const icons = {
    HIGH: <CheckCircleIcon sx={{ fontSize: 16, color: colors.HIGH.border }} />,
    MEDIUM: <WarningIcon sx={{ fontSize: 16, color: colors.MEDIUM.border }} />,
    LOW: <InfoIcon sx={{ fontSize: 16, color: colors.LOW.border }} />,
  };

  return (
    <Tooltip title={`Evidence confidence: ${percentage}%`}>
      <Chip
        icon={icons[level]}
        label={`${percentage}% Confidence`}
        size="small"
        sx={{
          backgroundColor: colors[level].bg,
          color: colors[level].text,
          border: `1px solid ${colors[level].border}`,
          fontWeight: 600,
          fontSize: '0.75rem',
        }}
      />
    </Tooltip>
  );
}

export function EvidenceDisplay({ evidenceSummary, fieldSuggestions, confidence }: EvidenceDisplayProps) {
  if (!evidenceSummary && !fieldSuggestions && !confidence) {
    return null;
  }

  return (
    <Card sx={{ mt: 2, border: '1px solid #E5E7EB' }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
          <AssessmentIcon sx={{ color: '#6366F1' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Evidence Analysis
          </Typography>
          {confidence !== undefined && <ConfidenceBadge confidence={confidence} />}
        </Stack>

        {evidenceSummary && (
          <>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Evidence-based validation powered by multi-source analysis and confidence scoring.
            </Typography>

            {/* Evidence Statistics */}
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Box sx={{ minWidth: 120 }}>
                  <Typography variant="caption" color="text.secondary">
                    Total Evidence
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {evidenceSummary.totalEvidence}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 120 }}>
                  <Typography variant="caption" color="text.secondary">
                    Capabilities
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {evidenceSummary.totalCapabilities}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 120 }}>
                  <Typography variant="caption" color="text.secondary">
                    Sources
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {evidenceSummary.totalSources}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Confidence Breakdown */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Evidence by Confidence Level
              </Typography>
              
              <Stack spacing={1}>
                {evidenceSummary.highConfidenceCount > 0 && (
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#10B981' }} />
                        <Typography variant="body2">HIGH (≥85%)</Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {evidenceSummary.highConfidenceCount}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={(evidenceSummary.highConfidenceCount / evidenceSummary.totalEvidence) * 100}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#D1FAE5',
                        '& .MuiLinearProgress-bar': { backgroundColor: '#10B981' },
                      }}
                    />
                  </Box>
                )}

                {evidenceSummary.mediumConfidenceCount > 0 && (
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <WarningIcon sx={{ fontSize: 16, color: '#F59E0B' }} />
                        <Typography variant="body2">MEDIUM (70-84%)</Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {evidenceSummary.mediumConfidenceCount}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={(evidenceSummary.mediumConfidenceCount / evidenceSummary.totalEvidence) * 100}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#FEF3C7',
                        '& .MuiLinearProgress-bar': { backgroundColor: '#F59E0B' },
                      }}
                    />
                  </Box>
                )}

                {evidenceSummary.lowConfidenceCount > 0 && (
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <InfoIcon sx={{ fontSize: 16, color: '#EF4444' }} />
                        <Typography variant="body2">LOW (&lt;70%)</Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {evidenceSummary.lowConfidenceCount}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={(evidenceSummary.lowConfidenceCount / evidenceSummary.totalEvidence) * 100}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#FEE2E2',
                        '& .MuiLinearProgress-bar': { backgroundColor: '#EF4444' },
                      }}
                    />
                  </Box>
                )}
              </Stack>
            </Box>

            {/* Capability Coverage */}
            {evidenceSummary.capabilitiesByLevel && Object.keys(evidenceSummary.capabilitiesByLevel).length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Capability Coverage
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {evidenceSummary.capabilitiesByLevel.STRONG !== undefined && evidenceSummary.capabilitiesByLevel.STRONG > 0 && (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label={`${evidenceSummary.capabilitiesByLevel.STRONG} STRONG`}
                        size="small"
                        sx={{
                          backgroundColor: '#D1FAE5',
                          color: '#065F46',
                          border: '1px solid #10B981',
                        }}
                      />
                    )}
                    {evidenceSummary.capabilitiesByLevel.MEDIUM !== undefined && evidenceSummary.capabilitiesByLevel.MEDIUM > 0 && (
                      <Chip
                        icon={<WarningIcon />}
                        label={`${evidenceSummary.capabilitiesByLevel.MEDIUM} MEDIUM`}
                        size="small"
                        sx={{
                          backgroundColor: '#FEF3C7',
                          color: '#92400E',
                          border: '1px solid #F59E0B',
                        }}
                      />
                    )}
                    {evidenceSummary.capabilitiesByLevel.WEAK !== undefined && evidenceSummary.capabilitiesByLevel.WEAK > 0 && (
                      <Chip
                        icon={<InfoIcon />}
                        label={`${evidenceSummary.capabilitiesByLevel.WEAK} WEAK`}
                        size="small"
                        sx={{
                          backgroundColor: '#FEE2E2',
                          color: '#991B1B',
                          border: '1px solid #EF4444',
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              </>
            )}
          </>
        )}

        {/* Field Suggestions */}
        {fieldSuggestions && fieldSuggestions.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Alert severity="info" icon={<TrendingUpIcon />} sx={{ mb: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Field Mapping Suggestions
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                These fields in your configuration are similar to expected fields:
              </Typography>
              <Stack spacing={1}>
                {fieldSuggestions.slice(0, 3).map((suggestion, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={`${suggestion.similarity}%`}
                      size="small"
                      sx={{
                        backgroundColor: suggestion.similarity >= 70 ? '#D1FAE5' : '#FEF3C7',
                        color: suggestion.similarity >= 70 ? '#065F46' : '#92400E',
                        fontWeight: 600,
                        minWidth: 50,
                      }}
                    />
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {suggestion.fieldName}
                    </Typography>
                    {index === 0 && (
                      <Chip label="Recommended" size="small" color="primary" />
                    )}
                  </Box>
                ))}
              </Stack>
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                Create a field mapping file to map your field names to standard compliance fields.
              </Typography>
            </Alert>
          </>
        )}
      </CardContent>
    </Card>
  );
}
